import React, { useMemo, useState, useEffect } from "react";
import { X } from "lucide-react";

/**
 * EmployeeDistributionChart — department sizes as a horizontal bar list.
 *
 * Department distribution is a MAGNITUDE comparison, so the form is a bar
 * chart with a single sequential hue (length carries the value; color adds
 * nothing) — not a many-slice donut. Real departments wear the brand hue;
 * "Unassigned" and the folded "Other" tail are de-emphasized in gray so the
 * organized departments stay the subject. Values ride the bar ends.
 */
const BRAND = { light: "#2a78d6", dark: "#4a9be0" };
const GRAY = { light: "#94a3b8", dark: "rgba(255,255,255,0.28)" };

const MAX_ROWS = 10;

const EmployeeDistributionChart = ({
  data = [], // [{ name, count }] sorted desc
  total = 0,
  theme = "light",
  language = "en",
}) => {
  const dark = theme === "dark";
  const isRTL = language === "ar";
  const [hover, setHover] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 60);
    return () => clearTimeout(t);
  }, []);

  const rows = useMemo(() => {
    const clean = (data || [])
      .map((d) => ({ name: String(d.name || "").trim() || "—", count: Number(d.count || 0) }))
      .filter((d) => d.count > 0);
    const head = clean.slice(0, MAX_ROWS);
    const tail = clean.slice(MAX_ROWS);
    if (tail.length) {
      head.push({
        name: isRTL ? `أخرى (${tail.length} أقسام)` : `Other (${tail.length} depts)`,
        count: tail.reduce((s, d) => s + d.count, 0),
        muted: true,
        isOther: true,
        items: tail,
      });
    }
    return head.map((d) => ({
      ...d,
      muted: d.muted || /^unassigned$/i.test(d.name),
      label: /^unassigned$/i.test(d.name) && isRTL ? "غير محدد" : d.name,
    }));
  }, [data, isRTL]);

  const grandTotal = total || rows.reduce((s, d) => s + d.count, 0) || 1;
  const maxCount = Math.max(1, ...rows.map((d) => d.count));
  const otherRow = rows.find((d) => d.isOther);

  const brand = dark ? BRAND.dark : BRAND.light;
  const gray = dark ? GRAY.dark : GRAY.light;
  const track = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)";
  const textPrimary = dark ? "#f1f5f9" : "#1e293b";
  const textSecondary = dark ? "rgba(255,255,255,0.55)" : "#64748b";

  return (
    <div style={{ minWidth: 0, direction: isRTL ? "rtl" : "ltr" }}>
      {/* summary strip */}
      <div
        style={{
          display: "flex",
          gap: 24,
          padding: "14px 2px 16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: textPrimary, lineHeight: 1.1 }}>
            {grandTotal}
          </div>
          <div style={{ fontSize: 11.5, color: textSecondary }}>
            {isRTL ? "إجمالي الموظفين" : "Total employees"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: textPrimary, lineHeight: 1.1 }}>
            {(data || []).filter((d) => Number(d.count) > 0 && !/^unassigned$/i.test(String(d.name).trim())).length}
          </div>
          <div style={{ fontSize: 11.5, color: textSecondary }}>
            {isRTL ? "قسماً" : "Departments"}
          </div>
        </div>
      </div>

      {/* bar list */}
      <div>
        {rows.map((d, i) => {
          const pct = (d.count / grandTotal) * 100;
          const scaled = (d.count / maxCount) * 100; // longest bar fills the track
          const dim = hover !== null && hover !== i;
          const color = d.muted ? gray : brand;
          return (
            <div
              key={d.label}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={d.isOther ? () => setOtherOpen(true) : undefined}
              title={
                d.isOther
                  ? (isRTL ? "اضغط لعرض التفاصيل" : "Click to see details")
                  : `${d.label}: ${d.count} (${pct.toFixed(1)}%)`
              }
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(90px, 34%) 1fr auto",
                alignItems: "center",
                gap: 12,
                padding: "5px 2px",
                opacity: dim ? 0.45 : 1,
                transition: "opacity .15s",
                cursor: d.isOther ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  color: d.muted ? textSecondary : textPrimary,
                  fontStyle: d.muted ? "italic" : "normal",
                  textDecoration: d.isOther ? "underline" : "none",
                  textDecorationStyle: "dotted",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {d.label}
              </div>

              <div
                style={{
                  height: 14,
                  borderRadius: isRTL ? "4px 0 0 4px" : "0 4px 4px 0",
                  background: track,
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: animated ? `${scaled}%` : "0%",
                    borderRadius: isRTL ? "4px 0 0 4px" : "0 4px 4px 0",
                    background: color,
                    transition: `width .7s cubic-bezier(.4,0,.2,1) ${i * 45}ms`,
                    [isRTL ? "marginLeft" : "marginRight"]: "auto",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: textPrimary,
                  whiteSpace: "nowrap",
                  minWidth: 58,
                  textAlign: isRTL ? "left" : "right",
                }}
              >
                {d.count}
                <span style={{ fontWeight: 400, color: textSecondary, marginInlineStart: 4 }}>
                  {Math.round(pct)}%
                </span>
              </div>
            </div>
          );
        })}

        {rows.length === 0 && (
          <div style={{ padding: "24px 0", fontSize: 13, color: textSecondary, textAlign: "center" }}>
            {isRTL ? "لا توجد بيانات أقسام" : "No department data"}
          </div>
        )}
      </div>

      {otherOpen && otherRow && (
        <div
          onClick={() => setOtherOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: dark ? "#1e293b" : "#fff",
              borderRadius: 14,
              padding: 20,
              width: "min(420px, 90vw)",
              maxHeight: "70vh",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: textPrimary }}>
                {otherRow.label}
              </h3>
              <button
                onClick={() => setOtherOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: textSecondary,
                  padding: 4,
                  display: "flex",
                }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: "0 0 12px", fontSize: 12.5, color: textSecondary }}>
              {isRTL
                ? `دي الأقسام الأصغر (${otherRow.items.length}) اللي بعد أكبر ${MAX_ROWS} أقسام، بنجمعها في صف واحد عشان القائمة تفضل مختصرة.`
                : `The ${otherRow.items.length} smallest departments (beyond the top ${MAX_ROWS}) are grouped into this row to keep the list compact.`}
            </p>

            <div>
              {otherRow.items
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((item) => {
                  const p = (item.count / grandTotal) * 100;
                  return (
                    <div
                      key={item.name}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "7px 0",
                        borderBottom: dark
                          ? "1px solid rgba(255,255,255,0.06)"
                          : "1px solid rgba(15,23,42,0.06)",
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: textPrimary }}>{item.name}</span>
                      <span style={{ color: textSecondary, whiteSpace: "nowrap" }}>
                        {item.count}{" "}
                        <span style={{ opacity: 0.7 }}>({p.toFixed(1)}%)</span>
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDistributionChart;
